/**
 * preferencesReducer.js
 * Talkroute
 *
 * Created by Vel Genov on 12/16/18
 * Copyright (c) 2018 Talkroute. All right reserved.
 */
import { REHYDRATE } from "redux-persist";

import {
  UPDATE_APP_NOW,
  CLEAR_UPDATE_STATE,
  FETCH_PHONE_NUMBERS_BEGIN,
  FETCH_PHONE_NUMBERS_SUCCESS,
  AUTO_UPDATE_CHECKBOX,
  SPELL_CHECK_CHECKBOX,
  OUTGOING_PHONE_NUMBER_SELECTED,
  OUTGOING_TEXT_PHONE_NUMBER_SELECTED,
  IS_AGREED_TERMS_CHANGED,
  FETCH_PHONE_NUMBERS_FAILURE,
  TOGGLE_TEST_MIC,
  SET_DEFAULT_AUDIO_INPUT,
  SET_DEFAULT_AUDIO_OUTPUT,
  SET_TEXT_NOTIFICATION_SOUND,
  SET_PREFERENCES_TABLE_SIZE,
  SET_CALL_NOTIFICATION_SOUND,
  SET_AUDIO_OUTPUT_VOLUME,
  SET_AUDIO_INPUT_VOLUME,
  SET_OPEN_PREFERENCES,
  DEFAULT_AUDIO_SOURCE,
  TOGGLE_VISIBILITY,
  CHECKED_FOR_UPDATES,
  UPDATE_TABLE_ROWS_PER_PAGE_DEFAULT,
  SET_OPEN_HISTORY_REPORTING,
  TOGGLE_HISTORY_REPORTING_VISIBILITY,
} from "../actions/preferencesActions";
import { TalkroutePhoneNumber, talkroutePhoneNumberRehydrator } from "../services/dataClasses/TalkroutePhoneNumber";
import { CLEAR_ERRORS } from "../actions/errorActions";
import { RowsPerPageOptions } from "@models/AppPreference.models";
import { ROWS_PER_PAGE_DEFAULT_VALUE } from "@constants/appConstants";


export type IPreferencesTableSize = {
  callHistory: RowsPerPageOptions;
  textMessaging: RowsPerPageOptions;
  forwardingPhoneNumbers: RowsPerPageOptions;
  transferCodes: RowsPerPageOptions;
  hoursOfOperations: RowsPerPageOptions;
  greetingsAudio: RowsPerPageOptions;
  voicemailboxes: RowsPerPageOptions;
  blockedNumbers: RowsPerPageOptions;
  contacts: RowsPerPageOptions;
  textMessageHistory: RowsPerPageOptions;
};

export type IPreferencesTableSizeKey = keyof IPreferencesTableSize;
export interface IPreferencesReducer {
  autoUpdatePermission: boolean;
  autoSpellCheck: boolean;
  outgoingPhoneNumbers: TalkroutePhoneNumber[];
  outgoingTextPhoneNumbers: TalkroutePhoneNumber[];
  allPhoneNumbers: TalkroutePhoneNumber[];
  openPreferences: boolean;
  openHistoryAndReporting: boolean;
  error: null | string;
  selectedOutgoingPhoneNumber: null | string;
  selectedOutgoingTextNumber: null | string;
  isEnabledTestMic: boolean;
  defaultAudioInput: string; // maybe can be done better
  defaultAudioOutput: string; // maybe can be done better
  selectedTextNotificationSound: number;
  selectedCallNotificationSound: number;
  defaultAudioOutputVolume: number;
  defaultAudioInputVolume: number;
  updateNow: boolean;
  isPreferencesVisible: boolean;
  isHistoryAndReportingVisible: boolean;
  lastUpdateCheckTimestamp: null | number;
  isAgreedTerms: boolean;
  rowsPerPage: RowsPerPageOptions;
  tableSize: IPreferencesTableSize;
}

const initialState = {
  autoUpdatePermission: true,
  autoSpellCheck: false,
  outgoingPhoneNumbers: [],
  outgoingTextPhoneNumbers: [],
  allPhoneNumbers: [],
  openPreferences: false,
  openHistoryAndReporting: false,
  error: null,
  selectedOutgoingPhoneNumber: null,
  selectedOutgoingTextNumber: null,
  isEnabledTestMic: false,
  defaultAudioInput: DEFAULT_AUDIO_SOURCE,
  defaultAudioOutput: DEFAULT_AUDIO_SOURCE,
  selectedTextNotificationSound: 0,
  selectedCallNotificationSound: 0,
  defaultAudioOutputVolume: 1,
  defaultAudioInputVolume: 1,
  updateNow: false,
  isPreferencesVisible: false,
  isHistoryAndReportingVisible: false,
  lastUpdateCheckTimestamp: null,
  isAgreedTerms: false,
  rowsPerPage: ROWS_PER_PAGE_DEFAULT_VALUE,
  tableSize: {
    callHistory: ROWS_PER_PAGE_DEFAULT_VALUE,
    textMessaging: ROWS_PER_PAGE_DEFAULT_VALUE,
    forwardingPhoneNumbers: ROWS_PER_PAGE_DEFAULT_VALUE,
    transferCodes: ROWS_PER_PAGE_DEFAULT_VALUE,
    hoursOfOperations: ROWS_PER_PAGE_DEFAULT_VALUE,
    greetingsAudio: ROWS_PER_PAGE_DEFAULT_VALUE,
    voicemailboxes: ROWS_PER_PAGE_DEFAULT_VALUE,
    blockedNumbers: ROWS_PER_PAGE_DEFAULT_VALUE,
    contacts: ROWS_PER_PAGE_DEFAULT_VALUE,
    textMessageHistory: ROWS_PER_PAGE_DEFAULT_VALUE,
  }
}

/**
 * Called to process user actions
 *
 * @param {*} state
 * @param {*} action
 */
export default function preferences(state: IPreferencesReducer = initialState, action): IPreferencesReducer {
  switch (action.type) {
    case REHYDRATE:
      // A redux-persist action. Called immediately after launch. TalkroutePhoneNumber objects are restored as generic Object by default.
      // Using a rehydrator function to restore to TalkroutePhoneNumber objects. Rehydrator checks for empty objects.
      // defaultAudioInput and defaultAudioOutput are not rehydrated on purpose. They should be set to DEFAULT_AUDIO_SOURCE when the app starts
      if (action.payload !== undefined) {
        return {
          ...state,
          autoUpdatePermission: action.payload.preferences.autoUpdatePermission,
          autoSpellCheck: action.payload.preferences.autoSpellCheck,
          outgoingPhoneNumbers: talkroutePhoneNumberRehydrator(
            action.payload.preferences.outgoingPhoneNumbers
          ),
          outgoingTextPhoneNumbers: talkroutePhoneNumberRehydrator(
            action.payload.preferences.outgoingTextPhoneNumbers
          ),
          allPhoneNumbers: talkroutePhoneNumberRehydrator(
            action.payload.preferences.allPhoneNumbers
          ),
          selectedOutgoingPhoneNumber:
            action.payload.preferences.selectedOutgoingPhoneNumber,
          selectedOutgoingTextNumber:
            action.payload.preferences.selectedOutgoingTextNumber,
          isEnabledTestMic: action.payload.preferences.isEnabledTestMic,
          selectedTextNotificationSound:
            action.payload.preferences.selectedTextNotificationSound,
          selectedCallNotificationSound:
            action.payload.preferences.selectedCallNotificationSound,
          updateNow: false,
          lastUpdateCheckTimestamp:
            action.payload.preferences.lastUpdateCheckTimestamp,
          error: null,
          isAgreedTerms: action.payload.preferences.isAgreedTerms,
          defaultAudioOutputVolume:
            action.payload.preferences.defaultAudioOutputVolume !== undefined
              ? action.payload.preferences.defaultAudioOutputVolume
              : 1,
          defaultAudioInputVolume:
            action.payload.preferences.defaultAudioInputVolume !== undefined
              ? action.payload.preferences.defaultAudioInputVolume
              : 1,
          rowsPerPage: action.payload.preferences.rowsPerPage || state.rowsPerPage,
          tableSize: {...initialState.tableSize, ...action.payload.preferences.tableSize} 
          || {...initialState.tableSize, ...state.tableSize}
        };
      }

      return {
        ...state,
      };
    case AUTO_UPDATE_CHECKBOX:
      // The state of the auto update checkbox has changed
      return {
        ...state,
        autoUpdatePermission: action.payload.value,
      };
    case SPELL_CHECK_CHECKBOX:
      // The state of the auto update checkbox has changed
      return {
        ...state,
        autoSpellCheck: action.payload.value,
      };
    case OUTGOING_PHONE_NUMBER_SELECTED:
      // The user has selectged a new outgoing phone number
      return {
        ...state,
        selectedOutgoingPhoneNumber: action.payload.value,
      };
    case OUTGOING_TEXT_PHONE_NUMBER_SELECTED:
      // The user has selected a new outgoing text phone number
      return {
        ...state,
        selectedOutgoingTextNumber: action.payload.value,
      };
    case IS_AGREED_TERMS_CHANGED:
      // The user has checked the agree terms
      return {
        ...state,
        isAgreedTerms: action.payload.value,
      };
    case FETCH_PHONE_NUMBERS_BEGIN:
      // Starting to fetch phone numbers
      return {
        ...state,
      };
    case FETCH_PHONE_NUMBERS_SUCCESS:
      // Fetching phone numbers was successful
      return {
        ...state,
        outgoingPhoneNumbers: action.payload.outgoingPhoneNumbers,
        outgoingTextPhoneNumbers: action.payload.outgoingTextPhoneNumbers,
        allPhoneNumbers: action.payload.allPhoneNumbers,
      };
    case FETCH_PHONE_NUMBERS_FAILURE:
      // There was a problem fetching phone numbers
      return {
        ...state,
        error: action.payload.error,
      };
    case UPDATE_APP_NOW:
      // The user has requested to check for and install an application update
      return {
        ...state,
        updateNow: true,
      };
    case CLEAR_UPDATE_STATE:
      // Clear the application update request status
      return {
        ...state,
        updateNow: false,
      };
    case TOGGLE_TEST_MIC:
      // Set the Test Mic enabled or not
      return {
        ...state,
        isEnabledTestMic: action.payload.isEnabledTestMic,
      };
    case SET_DEFAULT_AUDIO_INPUT:
      return {
        ...state,
        defaultAudioInput: action.payload.defaultAudioInput,
      };
    case SET_DEFAULT_AUDIO_OUTPUT:
      return {
        ...state,
        defaultAudioOutput: action.payload.defaultAudioOutput,
      };
    case SET_TEXT_NOTIFICATION_SOUND:
      // Set the notification sound
      return {
        ...state,
        selectedTextNotificationSound: action.payload.notificationSound,
      };
    case SET_OPEN_PREFERENCES:
      // Set if preferences page is open
      return {
        ...state,
        openPreferences: action.payload,
      };
    case SET_OPEN_HISTORY_REPORTING:
      return {
        ...state,
        openHistoryAndReporting: action.payload,
      }
    case UPDATE_TABLE_ROWS_PER_PAGE_DEFAULT:
      return {
        ...state,
        rowsPerPage: action.payload
      }
    case SET_CALL_NOTIFICATION_SOUND:
      // Set the notification sound
      return {
        ...state,
        selectedCallNotificationSound: action.payload.notificationSound,
      };
    case SET_AUDIO_OUTPUT_VOLUME:
      // Set the notification sound
      return {
        ...state,
        defaultAudioOutputVolume: action.payload.audioOutputVolume,
      };
    case SET_AUDIO_INPUT_VOLUME:
      // Set the notification sound
      return {
        ...state,
        defaultAudioInputVolume: action.payload.audioInputVolume,
      };
    case CLEAR_ERRORS:
      // Clear any errors
      return {
        ...state,
        error: null,
      };
    case TOGGLE_VISIBILITY:
      // Change the visibility state of the preferences window
      return {
        ...state,
        isPreferencesVisible: action.payload.isPreferencesVisible,
      };
    case TOGGLE_HISTORY_REPORTING_VISIBILITY:
      return {
        ...state,
        isHistoryAndReportingVisible: action.payload.isHistoryAndReportingVisible,
      }
    case CHECKED_FOR_UPDATES:
      // The last time the application checked for updates
      return {
        ...state,
        lastUpdateCheckTimestamp: action.payload,
      };
    case SET_PREFERENCES_TABLE_SIZE:
      return {
        ...state,
        tableSize: action.payload
      }
    default:
      return state;
  }
}
